/**
 * Field Schema Factories
 *
 * Factory functions for creating reusable Yup field schemas.
 * These are designed to be v1.x-compatible by using .nullable() BEFORE .required().
 */
import * as Yup from "yup";
import type {
  RequiredStringOptions,
  OptionalStringOptions,
  RequiredNumberOptions,
  OptionalNumberOptions,
  RequiredArrayOptions,
  FileItem,
  RequiredFileArrayOptions,
  OptionalFileArrayOptions,
} from "./types";

/**
 * Creates a required string field schema.
 *
 * @param messageOrOptions - Error message string or options object
 * @returns Yup StringSchema configured as nullable + required
 *
 * @example
 * // Simple usage with just a message
 * name: requiredString("Please provide a name")
 *
 * // With length constraints
 * code: requiredString({
 *   message: "Please provide a code",
 *   minLength: 2,
 *   maxLength: 10
 * })
 */
export const requiredString = (
  messageOrOptions: string | RequiredStringOptions
): Yup.Schema<string | null> => {
  const options: RequiredStringOptions =
    typeof messageOrOptions === "string" ? { message: messageOrOptions } : messageOrOptions;

  let schema = Yup.string().nullable().required(options.message);

  if (options.minLength !== undefined) {
    schema = schema.min(
      options.minLength,
      options.minMessage ?? `Must be at least ${options.minLength} characters`
    );
  }

  if (options.maxLength !== undefined) {
    schema = schema.max(
      options.maxLength,
      options.maxMessage ?? `Cannot exceed ${options.maxLength} characters`
    );
  }

  return schema;
};

/**
 * Creates an optional string field schema.
 *
 * @param options - Optional configuration for length constraints
 * @returns Yup StringSchema configured as nullable
 *
 * @example
 * // Simple optional string
 * notes: optionalString()
 *
 * // With length constraint
 * description: optionalString({ maxLength: 500 })
 */
export const optionalString = (
  options?: OptionalStringOptions
): Yup.Schema<string | null | undefined> => {
  let schema = Yup.string().nullable();

  if (options?.minLength !== undefined) {
    schema = schema.min(
      options.minLength,
      options.minMessage ?? `Must be at least ${options.minLength} characters`
    );
  }

  if (options?.maxLength !== undefined) {
    schema = schema.max(
      options.maxLength,
      options.maxMessage ?? `Cannot exceed ${options.maxLength} characters`
    );
  }

  return schema;
};

/**
 * Creates a required email field schema.
 *
 * @param message - Error message when email is missing
 * @param invalidMessage - Error message when email format is invalid
 * @returns Yup StringSchema configured for email validation
 *
 * @example
 * email: requiredEmail("Please provide an email", "Please provide a valid email")
 */
export const requiredEmail = (
  message: string,
  invalidMessage: string = "Please provide a valid email address"
): Yup.Schema<string | null> => {
  return Yup.string().nullable().email(invalidMessage).required(message);
};

/**
 * Creates an optional email field schema.
 *
 * @param invalidMessage - Error message when email format is invalid
 * @returns Yup StringSchema configured for optional email validation
 *
 * @example
 * secondaryEmail: optionalEmail("Please provide a valid email")
 */
export const optionalEmail = (
  invalidMessage: string = "Please provide a valid email address"
): Yup.Schema<string | null | undefined> => {
  return Yup.string().nullable().email(invalidMessage);
};

/**
 * Creates a required number field schema.
 *
 * @param messageOrOptions - Error message string or options object
 * @returns Yup NumberSchema configured as nullable + required
 *
 * @example
 * // Simple usage
 * quantity: requiredNumber("Please provide a quantity")
 *
 * // With min/max constraints
 * age: requiredNumber({
 *   message: "Please provide an age",
 *   min: 0,
 *   max: 150
 * })
 */
export const requiredNumber = (
  messageOrOptions: string | RequiredNumberOptions
): Yup.Schema<number | null> => {
  const options: RequiredNumberOptions =
    typeof messageOrOptions === "string" ? { message: messageOrOptions } : messageOrOptions;

  let schema = Yup.number().nullable().required(options.message);

  if (options.typeErrorMessage !== undefined) {
    schema = schema.typeError(options.typeErrorMessage);
  }

  if (options.min !== undefined) {
    schema = schema.min(options.min, options.minMessage ?? `Must be at least ${options.min}`);
  }

  if (options.max !== undefined) {
    schema = schema.max(options.max, options.maxMessage ?? `Cannot exceed ${options.max}`);
  }

  return schema;
};

/**
 * Creates an optional number field schema.
 *
 * @param options - Optional configuration for min/max constraints
 * @returns Yup NumberSchema configured as nullable
 *
 * @example
 * // Simple optional number
 * discount: optionalNumber()
 *
 * // With constraints
 * percentage: optionalNumber({ min: 0, max: 100 })
 */
export const optionalNumber = (
  options?: OptionalNumberOptions
): Yup.Schema<number | null | undefined> => {
  let schema = Yup.number().nullable();

  if (options?.typeErrorMessage !== undefined) {
    schema = schema.typeError(options.typeErrorMessage);
  }

  if (options?.min !== undefined) {
    schema = schema.min(options.min, options.minMessage ?? `Must be at least ${options.min}`);
  }

  if (options?.max !== undefined) {
    schema = schema.max(options.max, options.maxMessage ?? `Cannot exceed ${options.max}`);
  }

  return schema;
};

/**
 * Creates a required ID field schema (number).
 * Commonly used for foreign key relationships.
 *
 * @param message - Error message when ID is missing
 * @returns Yup NumberSchema configured as nullable + required
 *
 * @example
 * projectId: requiredId("Please select a project")
 * siteId: requiredId("Please select a site")
 */
export const requiredId = (message: string): Yup.Schema<number | null> => {
  return Yup.number().nullable().required(message);
};

/**
 * Creates an optional ID field schema (number).
 * Commonly used for optional foreign key relationships.
 *
 * @returns Yup NumberSchema configured as nullable
 *
 * @example
 * parentProjectId: optionalId()
 */
export const optionalId = (): Yup.Schema<number | null | undefined> => {
  return Yup.number().nullable();
};

/**
 * Creates a required boolean field schema.
 *
 * @param message - Error message when boolean is missing
 * @returns Yup BooleanSchema configured as nullable + required
 *
 * @example
 * isActive: requiredBoolean("Please specify if active")
 */
export const requiredBoolean = (message: string): Yup.Schema<boolean | null> => {
  return Yup.boolean().nullable().required(message);
};

/**
 * Creates an optional boolean field schema.
 *
 * @returns Yup BooleanSchema configured as nullable
 *
 * @example
 * bSuppressed: optionalBoolean()
 * bActive: optionalBoolean()
 */
export const optionalBoolean = (): Yup.Schema<boolean | null | undefined> => {
  return Yup.boolean().nullable();
};

/**
 * Creates an optional mixed field schema.
 * Useful for fields that can accept any type (e.g., file uploads).
 *
 * @returns Yup MixedSchema configured as nullable
 *
 * @example
 * imageURL: optionalMixed()
 * attachment: optionalMixed()
 */
export const optionalMixed = (): Yup.Schema<unknown> => {
  return Yup.mixed().nullable();
};

/**
 * Creates a required date field schema.
 *
 * @param message - Error message when date is missing
 * @returns Yup DateSchema configured as nullable + required
 *
 * @example
 * startDate: requiredDate("Please provide a start date")
 */
export const requiredDate = (message: string): Yup.Schema<Date | null> => {
  return Yup.date().nullable().required(message);
};

/**
 * Creates an optional date field schema.
 *
 * @returns Yup DateSchema configured as nullable
 *
 * @example
 * endDate: optionalDate()
 */
export const optionalDate = (): Yup.Schema<Date | null | undefined> => {
  return Yup.date().nullable();
};

/**
 * Creates a required array field schema.
 *
 * @param messageOrOptions - Error message string or options object
 * @returns Yup ArraySchema configured as nullable + required
 *
 * @example
 * // Simple required array
 * tags: requiredArray("Please provide at least one tag")
 *
 * // With item schema and min length
 * siteIds: requiredArray({
 *   message: "Please select at least one site",
 *   itemSchema: Yup.number(),
 *   minLength: 1
 * })
 */
export const requiredArray = <T = unknown>(
  messageOrOptions: string | RequiredArrayOptions<T>
): Yup.Schema<T[] | null | undefined> => {
  const options: RequiredArrayOptions<T> =
    typeof messageOrOptions === "string" ? { message: messageOrOptions } : messageOrOptions;

  let schema = options.itemSchema
    ? Yup.array().of(options.itemSchema as Yup.Schema<T>)
    : Yup.array();

  const nullableSchema = schema.nullable().required(options.message);

  if (options.minLength !== undefined) {
    return nullableSchema.min(
      options.minLength,
      options.minMessage ?? `Must have at least ${options.minLength} item(s)`
    ) as Yup.Schema<T[] | null | undefined>;
  }

  return nullableSchema as Yup.Schema<T[] | null | undefined>;
};

/**
 * Creates an optional array field schema.
 *
 * @param itemSchema - Optional schema for array items
 * @returns Yup ArraySchema configured as nullable
 *
 * @example
 * // Simple optional array
 * notes: optionalArray()
 *
 * // With typed items
 * userIds: optionalArray(Yup.number())
 */
export const optionalArray = <T = unknown>(
  itemSchema?: Yup.Schema<T>
): Yup.Schema<T[] | null | undefined> => {
  const schema = itemSchema ? Yup.array().of(itemSchema as Yup.Schema<T>) : Yup.array();

  return schema.nullable() as Yup.Schema<T[] | null | undefined>;
};

/**
 * Creates a required file array field schema.
 *
 * Validates that an array of file objects exists and is not empty.
 * Each file object is validated to ensure it has required properties: file and fileName.
 *
 * @param messageOrOptions - Error message string or options object
 * @returns Yup ArraySchema configured to validate FileItem objects
 *
 * @example
 * // Simple required file array
 * documents: requiredFileArray("Please upload at least one file")
 *
 * // With custom minimum length
 * attachments: requiredFileArray({
 *   message: "Please upload file(s)",
 *   minLength: 2,
 *   minMessage: "Please upload at least 2 files"
 * })
 */
export const requiredFileArray = (
  messageOrOptions: string | RequiredFileArrayOptions
): Yup.Schema<FileItem[] | null | undefined> => {
  const options: RequiredFileArrayOptions =
    typeof messageOrOptions === "string" ? { message: messageOrOptions } : messageOrOptions;

  // Yup schema to validate individual file object structure
  const fileItemSchema = Yup.object<FileItem>().shape({
    file: Yup.mixed().required("File object is required"),
    fileName: Yup.string().required("File name is required"),
    fileDownloadUri: Yup.string().nullable().optional(),
    fileExtension: Yup.string().nullable().optional(),
  });

  const customItemSchema = options.itemSchema || fileItemSchema;
  let schema = Yup.array().of(customItemSchema as Yup.Schema<FileItem>);

  const nullableSchema = schema.nullable().required(options.message).min(1, options.message);

  if (options.minLength !== undefined) {
    return nullableSchema.min(
      options.minLength,
      options.minMessage ?? `Must have at least ${options.minLength} file(s)`
    ) as Yup.Schema<FileItem[] | null | undefined>;
  }

  return nullableSchema as Yup.Schema<FileItem[] | null | undefined>;
};

/**
 * Creates an optional file array field schema.
 *
 * Validates that an array of file objects is optional, but if provided,
 * each file object must have required properties: file and fileName.
 *
 * @param options - Optional configuration with custom itemSchema
 * @returns Yup ArraySchema configured to validate FileItem objects
 *
 * @example
 * // Simple optional file array
 * attachments: optionalFileArray()
 *
 * // With custom schema for items
 * documents: optionalFileArray({
 *   itemSchema: customFileValidation
 * })
 */
export const optionalFileArray = (
  options?: OptionalFileArrayOptions
): Yup.Schema<FileItem[] | null | undefined> => {
  // Yup schema to validate individual file object structure
  const fileItemSchema = Yup.object<FileItem>().shape({
    file: Yup.mixed().required("File object is required"),
    fileName: Yup.string().required("File name is required"),
    fileDownloadUri: Yup.string().nullable().optional(),
    fileExtension: Yup.string().nullable().optional(),
  });

  const customItemSchema = options?.itemSchema || fileItemSchema;
  const schema = Yup.array().of(customItemSchema as Yup.Schema<FileItem>);

  return schema.nullable() as Yup.Schema<FileItem[] | null | undefined>;
};
